import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Users as UsersIcon,
  DollarSign,
  Activity,
  Send as SendIcon,
  Sparkles,
  TrendingDown,
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertCircle,
  UserPlus,
  CreditCard as CardIcon,
  
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart as RLineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { getAdminOverview, pingDataSources, getAiUsageStats, getApiCostBreakdown, setAnthropicDailyCap, getDailyHealthCheck } from "@/lib/admin-overview.functions";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";


export const Route = createFileRoute("/_authenticated/admin/")({
  component: AdminOverview,
});

const PLAN_COLORS: Record<string, string> = {
  free: "hsl(220 10% 50%)",
  trial: "hsl(45 95% 60%)",
  pro: "hsl(195 90% 55%)",
  elite: "hsl(280 80% 65%)",
};

function AdminOverview() {
  const fetchOverview = useServerFn(getAdminOverview);
  const fetchPings = useServerFn(pingDataSources);
  const fetchAiUsage = useServerFn(getAiUsageStats);
  const fetchCostBreakdown = useServerFn(getApiCostBreakdown);

  const overview = useQuery({
    queryKey: ["admin", "overview"],
    queryFn: () => fetchOverview(),
    refetchInterval: 60_000,
  });

  const pings = useQuery({
    queryKey: ["admin", "pings"],
    queryFn: () => fetchPings(),
    refetchInterval: 5 * 60_000,
  });

  const aiUsage = useQuery({
    queryKey: ["admin", "ai-usage"],
    queryFn: () => fetchAiUsage(),
    refetchInterval: 60_000,
  });

  const costBreakdown = useQuery({
    queryKey: ["admin", "cost-breakdown"],
    queryFn: () => fetchCostBreakdown(),
    refetchInterval: 60_000,
  });

  const fetchHealth = useServerFn(getDailyHealthCheck);
  const health = useQuery({
    queryKey: ["admin", "daily-health"],
    queryFn: () => fetchHealth(),
    refetchInterval: 60_000,
  });

  const m = overview.data;
  const h = health.data;

  return (
    <div className="space-y-6">
      {/* Header + refresh */}
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-lg font-bold tracking-tight">Command Center</h2>
          <p className="text-xs text-muted-foreground">Live platform metrics and activity.</p>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="h-8 gap-1.5 text-xs"
          onClick={() => {
            overview.refetch();
            pings.refetch();
          }}
          disabled={overview.isFetching}
        >
          <RefreshCw className={`h-3 w-3 ${overview.isFetching ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Row 1 — 6 metric cards */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <MetricCard
          loading={overview.isLoading}
          label="Total Users"
          value={m?.totalUsers}
          icon={UsersIcon}
        />
        <MetricCard
          loading={overview.isLoading}
          label="MRR"
          value={m ? `$${m.mrrUsd.toLocaleString()}` : undefined}
          icon={DollarSign}
          accent="text-buy"
        />
        <MetricCard
          loading={overview.isLoading}
          label="Active Today"
          value={m?.activeToday}
          icon={Activity}
        />
        <MetricCard
          loading={overview.isLoading}
          label="Signals Fired"
          value={m?.signalsFired}
          icon={SendIcon}
          accent="text-primary"
        />
        <MetricCard
          loading={overview.isLoading}
          label="Trial Users"
          value={m?.trialUsers}
          icon={Sparkles}
          accent="text-warn"
        />
        <MetricCard
          loading={overview.isLoading}
          label="Churn (mo)"
          value={m?.churnThisMonth}
          icon={TrendingDown}
          accent="text-sell"
        />
      </div>

      {/* Daily Health Check — beta monitoring checklist */}
      <Card className="border-border/60 bg-card/60 p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Daily Health Check {h ? `· ${h.date}` : ""}
          </h3>
          {h && (
            <span
              className={`text-[10px] font-bold uppercase ${
                h.circuitsAllGreen && h.failedPayments === 0 && h.auditErrors === 0
                  ? "text-buy"
                  : "text-warn"
              }`}
            >
              {h.circuitsAllGreen && h.failedPayments === 0 && h.auditErrors === 0
                ? "All systems normal"
                : "Attention required"}
            </span>
          )}
        </div>
        {health.isLoading || !h ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          <ul className="grid gap-2 text-xs sm:grid-cols-2">
            <HealthRow label="New signups today" value={h.newSignups} />
            <HealthRow label="Active paid users" value={h.activePaidUsers} />
            <HealthRow label="Telegram signals sent" value={h.telegramSignalsSent} />
            <HealthRow
              label="Anthropic cost today"
              value={`$${h.anthropicCostUsd.toFixed(2)}`}
              tone={h.anthropicCostUsd > 50 ? "warn" : "ok"}
            />
            <HealthRow
              label="Failed payments"
              value={h.failedPayments}
              tone={h.failedPayments > 0 ? "bad" : "ok"}
            />
            <HealthRow
              label="Errors in audit log"
              value={h.auditErrors}
              tone={h.auditErrors > 0 ? "bad" : "ok"}
            />
            <HealthRow
              label="Circuit breakers"
              value={h.circuitsAllGreen ? "All green" : `Open: ${h.openCircuits.join(", ")}`}
              tone={h.circuitsAllGreen ? "ok" : "bad"}
            />
          </ul>
        )}
        <MailboxChecklist />
      </Card>

      {/* AI cost observability */}
      <div>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          AI usage (today, UTC)
        </h3>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <MetricCard
            loading={aiUsage.isLoading}
            label="API Calls"
            value={
              aiUsage.data
                ? `${aiUsage.data.calls} / est. $${aiUsage.data.est_cost_usd.toFixed(2)}`
                : undefined
            }
            icon={Sparkles}
            accent="text-primary"
          />
          <MetricCard
            loading={aiUsage.isLoading}
            label="Avg Response"
            value={aiUsage.data ? `${(aiUsage.data.avg_latency_ms / 1000).toFixed(1)}s` : undefined}
            icon={Activity}
          />
          <MetricCard
            loading={aiUsage.isLoading}
            label="Fallback Rate"
            value={
              aiUsage.data ? `${Math.round(aiUsage.data.fallback_rate * 100)}%` : undefined
            }
            icon={AlertCircle}
            accent={
              aiUsage.data && aiUsage.data.fallback_rate > 0.2 ? "text-sell" : "text-buy"
            }
          />
          <MetricCard
            loading={aiUsage.isLoading}
            label="Tokens (in/out)"
            value={
              aiUsage.data
                ? `${aiUsage.data.input_tokens.toLocaleString()} / ${aiUsage.data.output_tokens.toLocaleString()}`
                : undefined
            }
            icon={SendIcon}
          />
        </div>
      </div>

      {/* Cost observability — per-service usage vs daily caps */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Cost observability (today, UTC)
          </h3>
          {costBreakdown.data?.alerts && costBreakdown.data.alerts.length > 0 ? (
            <span className="text-[10px] font-semibold text-sell">
              {costBreakdown.data.alerts.length} alert{costBreakdown.data.alerts.length > 1 ? "s" : ""}
            </span>
          ) : (
            <span className="text-[10px] text-buy">No alerts today</span>
          )}
        </div>

        {/* Anthropic hard daily $ cap — admin configurable */}
        <AnthropicHardCapCard
          cap={costBreakdown.data?.anthropicHardCap}
          onChanged={() => costBreakdown.refetch()}
        />

        <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
          {costBreakdown.isLoading
            ? Array.from({ length: 7 }).map((_, i) => (
                <Card key={i} className="border-border/60 bg-card/40 p-4">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="mt-3 h-5 w-16" />
                </Card>
              ))
            : costBreakdown.data?.rows.map((row) => <CostCard key={row.service} row={row} />)}
        </div>
      </div>




      {/* Row 2 — charts */}
      <div className="grid gap-3 lg:grid-cols-3">

        <ChartCard title="MRR growth (30d)">
          {overview.isLoading || !m ? (
            <Skeleton className="h-48 w-full" />
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <RLineChart data={m.mrr30d}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.3} />
                <XAxis dataKey="date" stroke="var(--muted-foreground)" fontSize={10} />
                <YAxis stroke="var(--muted-foreground)" fontSize={10} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "var(--card)",
                    border: "1px solid var(--border)",
                    fontSize: 11,
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="mrr"
                  stroke="var(--primary)"
                  strokeWidth={2}
                  dot={false}
                />
              </RLineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Signups (14d)">
          {overview.isLoading || !m ? (
            <Skeleton className="h-48 w-full" />
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={m.signups14d}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.3} />
                <XAxis dataKey="date" stroke="var(--muted-foreground)" fontSize={10} />
                <YAxis stroke="var(--muted-foreground)" fontSize={10} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "var(--card)",
                    border: "1px solid var(--border)",
                    fontSize: 11,
                  }}
                />
                <Bar dataKey="count" fill="var(--primary)" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Plan distribution">
          {overview.isLoading || !m ? (
            <Skeleton className="h-48 w-full" />
          ) : m.planDistribution.length === 0 ? (
            <EmptyState text="No users yet" />
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={m.planDistribution}
                  dataKey="count"
                  nameKey="plan"
                  innerRadius={45}
                  outerRadius={75}
                  paddingAngle={2}
                >
                  {m.planDistribution.map((entry) => (
                    <Cell
                      key={entry.plan}
                      fill={PLAN_COLORS[entry.plan] ?? "hsl(220 10% 50%)"}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: "var(--card)",
                    border: "1px solid var(--border)",
                    fontSize: 11,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* Row 3 — data source health */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Data sources
          </h3>
          {pings.dataUpdatedAt > 0 && (
            <span className="text-[10px] text-muted-foreground">
              Last ping{" "}
              {new Date(pings.dataUpdatedAt).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
          {pings.isLoading
            ? Array.from({ length: 5 }).map((_, i) => (
                <Card key={i} className="border-border/60 bg-card/40 p-4">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="mt-3 h-5 w-16" />
                </Card>
              ))
            : pings.data?.sources.map((s) => (
                <SourceCard key={s.name} source={s} />
              ))}
        </div>
      </div>

      {/* Row 4 — recent activity */}
      <Card className="border-border/60 bg-card/60">
        <div className="border-b border-border/60 px-4 py-3">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Recent activity
          </h3>
        </div>
        <div className="divide-y divide-border/40">
          {overview.isLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="px-4 py-3">
                <Skeleton className="h-3 w-3/4" />
              </div>
            ))
          ) : m && m.activity.length > 0 ? (
            m.activity.map((evt) => <ActivityRow key={evt.id} evt={evt} />)
          ) : (
            <div className="px-4 py-8 text-center text-xs text-muted-foreground">
              No recent activity.
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

function MetricCard({
  label,
  value,
  icon: Icon,
  accent,
  loading,
}: {
  label: string;
  value: string | number | undefined;
  icon: typeof UsersIcon;
  accent?: string;
  loading?: boolean;
}) {
  return (
    <Card className="border-border/60 bg-card/60 p-4">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
          {label}
        </span>
        <Icon className={`h-3.5 w-3.5 ${accent ?? "text-primary"}`} />
      </div>
      <div className={`mt-2 font-mono text-2xl font-bold tabular-nums ${accent ?? ""}`}>
        {loading || value === undefined ? <Skeleton className="h-7 w-16" /> : value}
      </div>
    </Card>
  );
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card className="border-border/60 bg-card/60 p-4">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        {title}
      </h3>
      {children}
    </Card>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="flex h-48 items-center justify-center text-xs text-muted-foreground">
      {text}
    </div>
  );
}

function SourceCard({
  source,
}: {
  source: {
    name: string;
    status: "live" | "error" | "missing";
    latencyMs: number | null;
    detail: string;
    circuit?: { isOpen: boolean; failures: number; cooldownRemainingMs: number };
  };
}) {
  const Icon =
    source.status === "live" ? CheckCircle2 : source.status === "error" ? XCircle : AlertCircle;
  const color =
    source.status === "live"
      ? "text-buy"
      : source.status === "error"
        ? "text-sell"
        : "text-muted-foreground";
  const label = source.status === "live" ? "LIVE" : source.status === "error" ? "ERROR" : "NO KEY";

  const c = source.circuit;
  const circuitOpen = !!c?.isOpen;
  const degraded = !!c && !c.isOpen && c.failures > 0;
  const circuitLabel = circuitOpen
    ? `🔴 CIRCUIT OPEN · ${Math.ceil((c!.cooldownRemainingMs ?? 0) / 60000)}m left`
    : degraded
      ? `🟡 DEGRADED · ${c!.failures} fail${c!.failures > 1 ? "s" : ""}`
      : c
        ? "🟢 OK"
        : null;
  const circuitColor = circuitOpen
    ? "text-sell"
    : degraded
      ? "text-warn"
      : "text-buy";

  return (
    <Card className="border-border/60 bg-card/40 p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold">{source.name}</span>
        <Icon className={`h-3.5 w-3.5 ${color}`} />
      </div>
      <div className="mt-2 flex items-center justify-between">
        <span className={`text-[10px] font-bold uppercase tracking-wider ${color}`}>{label}</span>
        <span className="font-mono text-[10px] text-muted-foreground">
          {source.latencyMs !== null ? `${source.latencyMs}ms` : "—"}
        </span>
      </div>
      <div className="mt-1 truncate text-[10px] text-muted-foreground" title={source.detail}>
        {source.detail}
      </div>
      {circuitLabel && (
        <div className={`mt-1 text-[10px] font-semibold ${circuitColor}`}>{circuitLabel}</div>
      )}
    </Card>
  );
}

function ActivityRow({
  evt,
}: {
  evt: { type: "signup" | "upgrade" | "cancellation" | "signal"; label: string; email: string | null; at: string };
}) {
  const meta = {
    signup: { Icon: UserPlus, color: "text-buy", badge: "SIGNUP" },
    upgrade: { Icon: CardIcon, color: "text-primary", badge: "UPGRADE" },
    cancellation: { Icon: XCircle, color: "text-sell", badge: "CANCEL" },
    signal: { Icon: SendIcon, color: "text-warn", badge: "SIGNAL" },
  }[evt.type];
  const Icon = meta.Icon;

  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2.5">
      <div className="flex min-w-0 items-center gap-2.5">
        <Icon className={`h-3.5 w-3.5 shrink-0 ${meta.color}`} />
        <span
          className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${meta.color}`}
          style={{ backgroundColor: "color-mix(in oklab, currentColor 15%, transparent)" }}
        >
          {meta.badge}
        </span>
        <span className="truncate text-xs">{evt.label}</span>
        {evt.email && (
          <span className="truncate font-mono text-[10px] text-muted-foreground">
            · {evt.email}
          </span>
        )}
      </div>
      <span className="shrink-0 text-[10px] text-muted-foreground">
        {timeAgo(evt.at)}
      </span>
    </div>
  );
}

function CostCard({
  row,
}: {
  row: {
    service: string;
    calls: number;
    cost_usd: number;
    limit: number;
    kind: "calls" | "cost_usd";
    value: number;
    pct: number;
    status: "ok" | "warn" | "alert";
  };
}) {
  const color =
    row.status === "alert" ? "text-sell" : row.status === "warn" ? "text-warn" : "text-buy";
  const dot = row.status === "alert" ? "🔴" : row.status === "warn" ? "🟡" : "🟢";
  const pctLabel = `${Math.min(999, Math.round(row.pct * 100))}%`;
  const valueLabel =
    row.kind === "cost_usd"
      ? `$${row.value.toFixed(2)} / $${row.limit.toFixed(2)}`
      : `${row.value} / ${row.limit}`;
  const subLabel =
    row.kind === "cost_usd"
      ? `${row.calls} call${row.calls === 1 ? "" : "s"}`
      : `$${row.cost_usd.toFixed(2)} est.`;
  return (
    <Card className="border-border/60 bg-card/40 p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold capitalize">{row.service}</span>
        <span className="text-xs">{dot}</span>
      </div>
      <div className={`mt-2 font-mono text-sm font-bold tabular-nums ${color}`}>
        {valueLabel}
      </div>
      <div className="mt-1 flex items-center justify-between text-[10px] text-muted-foreground">
        <span>{subLabel}</span>
        <span className={`font-semibold ${color}`}>{pctLabel}</span>
      </div>
      <div className="mt-2 h-1 w-full overflow-hidden rounded bg-muted">
        <div
          className="h-full"
          style={{
            width: `${Math.min(100, Math.round(row.pct * 100))}%`,
            backgroundColor:
              row.status === "alert"
                ? "var(--sell)"
                : row.status === "warn"
                  ? "var(--warn)"
                  : "var(--buy)",
          }}
        />
      </div>
    </Card>
  );
}

function AnthropicHardCapCard({
  cap,
  onChanged,
}: {
  cap?: { cap_usd: number; spent_usd: number; capped: boolean; pct: number };
  onChanged: () => void;
}) {
  const save = useServerFn(setAnthropicDailyCap);
  const [draft, setDraft] = useState<string>("");
  const [saving, setSaving] = useState(false);

  const current = cap?.cap_usd ?? 20;
  const spent = cap?.spent_usd ?? 0;
  const pct = Math.min(1, cap?.pct ?? 0);
  const capped = cap?.capped ?? false;

  async function onSave() {
    const usd = Number(draft);
    if (!Number.isFinite(usd) || usd < 0) {
      toast.error("Enter a valid USD amount");
      return;
    }
    setSaving(true);
    try {
      await save({ data: { usd } });
      toast.success(`Daily cap set to $${usd.toFixed(2)}`);
      setDraft("");
      onChanged();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      className={`p-4 ${
        capped
          ? "border-sell/60 bg-sell/10"
          : pct > 0.8
            ? "border-warn/60 bg-warn/5"
            : "border-border/60 bg-card/40"
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Anthropic hard daily $ cap
            </span>
            {capped && (
              <span className="rounded bg-sell px-2 py-0.5 text-[10px] font-bold uppercase text-sell-foreground">
                🛑 Cap hit · Claude blocked
              </span>
            )}
          </div>
          <div className="mt-1 text-lg font-bold">
            ${spent.toFixed(2)} <span className="text-sm font-normal text-muted-foreground">/ ${current.toFixed(2)}</span>
          </div>
          <div className="mt-2 h-1.5 w-64 max-w-full overflow-hidden rounded bg-muted">
            <div
              className="h-full"
              style={{
                width: `${pct * 100}%`,
                background: capped ? "var(--sell)" : pct > 0.8 ? "var(--warn)" : "var(--buy)",
              }}
            />
          </div>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-muted-foreground">
              Set cap (USD)
            </label>
            <Input
              type="number"
              min={0}
              step="0.5"
              placeholder={String(current)}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="h-8 w-28"
            />
          </div>
          <Button size="sm" onClick={onSave} disabled={saving || !draft}>
            {saving ? "Saving…" : "Save cap"}
          </Button>
        </div>
      </div>
    </Card>
  );
}


function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const min = Math.floor(s / 60);
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function HealthRow({
  label,
  value,
  tone = "ok",
}: {
  label: string;
  value: string | number;
  tone?: "ok" | "warn" | "bad";
}) {
  const toneColor =
    tone === "bad" ? "text-sell" : tone === "warn" ? "text-warn" : "text-foreground";
  const dot =
    tone === "bad" ? "bg-sell" : tone === "warn" ? "bg-warn" : "bg-buy";
  return (
    <li className="flex items-center justify-between rounded border border-border/40 bg-card/40 px-3 py-2">
      <span className="flex items-center gap-2 text-muted-foreground">
        <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
        {label}
      </span>
      <span className={`font-mono font-semibold tabular-nums ${toneColor}`}>{value}</span>
    </li>
  );
}

const MAILBOXES = [
  "support@forexmind.ai",
  "billing@forexmind.ai",
  "legal@forexmind.ai",
] as const;

type MailboxState = Record<string, { tested: boolean; date: string }>;

function MailboxChecklist() {
  const [state, setState] = useState<MailboxState>(() => {
    if (typeof window === "undefined") return {};
    try {
      return JSON.parse(localStorage.getItem("fm.admin.mailbox-checklist") || "{}");
    } catch {
      return {};
    }
  });

  const update = (mb: string, tested: boolean) => {
    const next: MailboxState = {
      ...state,
      [mb]: { tested, date: tested ? new Date().toISOString().slice(0, 10) : "" },
    };
    setState(next);
    try {
      localStorage.setItem("fm.admin.mailbox-checklist", JSON.stringify(next));
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="mt-4 rounded-md border border-border/50 bg-card/30 p-3">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
          Inbox deliverability checklist
        </h4>
      </div>
      <ul className="grid gap-1.5 text-xs">
        {MAILBOXES.map((mb) => {
          const s = state[mb] || { tested: false, date: "" };
          return (
            <li
              key={mb}
              className="flex items-center justify-between gap-3 rounded border border-border/40 bg-card/40 px-3 py-2"
            >
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={s.tested}
                  onChange={(e) => update(mb, e.target.checked)}
                  className="h-3.5 w-3.5 accent-primary"
                />
                <span className="font-mono">{mb}</span>
              </label>
              <span
                className={`font-mono tabular-nums ${
                  s.tested ? "text-buy" : "text-warn"
                }`}
              >
                {s.tested ? `tested ${s.date}` : "not tested"}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="mt-2 text-[10.5px] leading-snug text-muted-foreground">
        Note: email forwarding and MX records for these mailboxes must be configured and
        verified manually at the DNS / email provider — this app cannot detect deliverability.
      </p>
    </div>
  );
}
